
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PropertyCard } from "@/components/real-estate/PropertyCard";
import { PropertyHero } from "@/components/real-estate/PropertyHero";
import { PropertyFilters } from "@/components/real-estate/PropertyFilters";
import { PropertyStats } from "@/components/real-estate/PropertyStats";
import { RealEstateNavBar } from "@/components/real-estate/RealEstateNavBar";
import type { Tables } from "@/integrations/supabase/types";
import { useToast } from "@/components/ui/use-toast";
import { Alert } from "@/components/ui/custom-alert";
import { ShieldCheck, Info } from "lucide-react";

type RealEstateProperty = Tables<"real_estate_properties">;

const RealEstate = () => {
  const [propertyType, setPropertyType] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [minInvestment, setMinInvestment] = useState(0);
  const [expectedReturns, setExpectedReturns] = useState(0);
  const [sortBy, setSortBy] = useState("recent");
  const { toast } = useToast();

  const { data: properties, isLoading } = useQuery({
    queryKey: ["real-estate-properties", propertyType, searchQuery, minInvestment, expectedReturns, sortBy],
    queryFn: async () => {
      let query = supabase
        .from("real_estate_properties")
        .select("*")
        .eq("status", "available");

      if (propertyType !== "all") {
        query = query.eq("property_type", propertyType);
      }

      if (searchQuery) {
        query = query.or(`title.ilike.%${searchQuery}%,location.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
      }

      if (minInvestment > 0) {
        query = query.gte("price", minInvestment);
      }

      // Use expectedReturns as a max price filter instead
      if (expectedReturns > 0) {
        const maxPrice = expectedReturns * 10000;
        query = query.lte("price", maxPrice);
      }

      // Apply sorting
      switch (sortBy) {
        case "popular":
          query = query.order("investment_metrics->total_investors", { ascending: false });
          break;
        case "returns":
          query = query.order("expected_returns", { ascending: false });
          break;
        case "price-low":
          query = query.order("price", { ascending: true });
          break;
        case "price-high":
          query = query.order("price", { ascending: false });
          break;
        case "recent":
        default:
          query = query.order("created_at", { ascending: false });
      }

      const { data, error } = await query;

      if (error) {
        toast({
          title: "Error",
          description: "Could not load properties",
          variant: "destructive",
        });
        throw error;
      }

      return data as RealEstateProperty[];
    },
  });

  const featuredProperties = properties?.filter(p => p.is_featured) || [];
  const regularProperties = properties?.filter(p => !p.is_featured) || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <RealEstateNavBar />
      <PropertyHero />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Alert variant="info" className="mb-6">
          <div className="flex items-center">
            <ShieldCheck className="h-5 w-5 mr-2 text-purple-600" />
            <p className="font-medium text-purple-800">
              All properties are verified and can be used as collateral for loans
            </p>
          </div>
        </Alert>
        
        {featuredProperties.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-800">Featured Properties</h2>
              <div className="ml-3 px-2 py-1 bg-amber-100 text-amber-800 rounded-md text-xs font-medium">Premium Selection</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          </div>
        )}

        <div id="property-listings" className="scroll-mt-20">
          <PropertyFilters
            propertyType={propertyType}
            setPropertyType={setPropertyType}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            minInvestment={minInvestment}
            setMinInvestment={setMinInvestment}
            expectedReturns={expectedReturns}
            setExpectedReturns={setExpectedReturns}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />

          <PropertyStats properties={properties || []} />

          <div className="mb-6 flex items-center">
            <h2 className="text-2xl font-bold text-slate-800">Available Properties</h2>
            <div className="ml-3 flex items-center">
              <Info className="h-4 w-4 text-slate-500 mr-1" />
              <span className="text-sm text-slate-500">
                {regularProperties.length} properties found
              </span>
            </div>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-[400px] animate-pulse bg-gray-100 rounded-lg" />
              ))}
            </div>
          ) : !regularProperties.length ? (
            <div className="text-center py-12 border border-dashed rounded-lg border-gray-200">
              <p className="text-muted-foreground">No properties match your search criteria</p>
              <button 
                className="mt-4 text-sm text-primary"
                onClick={() => {
                  setPropertyType("all");
                  setSearchQuery("");
                  setMinInvestment(0);
                  setExpectedReturns(0);
                  setSortBy("recent");
                }}
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {regularProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RealEstate;
